import React, { useCallback, useRef } from 'react';
import RsvpUnitWrapper from './RsvpUnitWrapper';
import { CalendarEvent } from 'mini-calendar-event-types';
import { deleteFromReadingPane } from 'mini-mail-reading-pane-view/lib/utils/deleteFromReadingPane';
import { format } from 'owa-strings';
import { getCalendarViewDetails } from 'mini-mail-reading-pane-store/lib/store/selectors/getCalendarViewDetails';
import { getOwaMiniStrings } from 'owa-strings/owa-mini-strings';
import { getRsvpUnitSubtitle } from 'mini-rsvp/lib/utils/getRsvpUnitSubtitle';
import { getRsvpUnitTitle } from 'mini-rsvp/lib/utils/getRsvpUnitTitle';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { isCurrentUserTheMeetingOrganizer } from 'mini-meeting-invite/lib/utils/isCurrentUserTheMeetingOrganizer';
import { isEffectivelyActionable } from 'mini-rsvp/lib/utils/isEffectivelyActionable';
import { lazyDeleteItemsWithoutConfirmDialog } from 'mini-mail-triage-actions';
import { MAIL_READING_PANE_VIEW } from 'data/lib/mini-app-constants/commonEventSourceConstants';
import { observer } from 'mobx-react-lite';
import { ReadingPaneRsvpUnitProps } from '../schema/ReadingPaneRsvpUnit.types';
import { removeItemServiceProvider } from '../utils/removeItemServiceProvider';
import { RsvpContextObject } from 'mini-rsvp/lib/utils/RsvpContextObject';
import { setRsvpUnitAriaDetailsForItem } from 'owa-mail-store/lib/actions/setRsvpUnitAriaDetailsForItem';
import { shouldMeetingItemDetailsShowRsvp } from 'mini-meeting-invite/lib/utils/shouldMeetingItemDetailsShowRsvp';
import { useTriageActionBehavior } from 'mini-mail-reading-pane-behavior-connect/lib/connectors/useTriageActionBehavior';

const ReadingPaneRsvpUnit = (props: ReadingPaneRsvpUnitProps) => {
    const { item } = props;

    const strings = getOwaMiniStrings();
    const ctx = RsvpContextObject;

    const rsvpViewedYet = useRef(false);

    const { latestMeetingItemIdToShowRsvp } = getCalendarViewDetails();

    const isCurrentUserOrganizer = isCurrentUserTheMeetingOrganizer(item);
    const isLatestMeetingItemToShowRsvp =
        !!latestMeetingItemIdToShowRsvp && latestMeetingItemIdToShowRsvp === item?.ItemId?.Id;
    const shouldFetchFullEvent = isLatestMeetingItemToShowRsvp && !isCurrentUserOrganizer;

    const triageActionBehavior = useTriageActionBehavior();
    const { onReadingPaneClose } = triageActionBehavior || {};

    const deleteMeetingMessageCallback = () => {
        const { item } = props;
        lazyDeleteItemsWithoutConfirmDialog({
            itemIds: [item.ItemId.Id],
            parentFolderId: item.ParentFolderId.Id,
            onReadingPaneClose,
        });
    };

    const logRsvpViewedDatapoint = useCallback(
        (calEvent: CalendarEvent) => {
            const responseType = item?.ResponseType || 'Remove';
            const isEffectivelyActionableProps = {
                isActionable: isLatestMeetingItemToShowRsvp,
                item: calEvent,
                originalMeetingMessage: item,
            };
            const customDataForRsvpViewed = {
                isUIAction: true,
                itemId: item?.ItemId?.Id,
                responseType: responseType,
                hasButton: isEffectivelyActionable(isEffectivelyActionableProps),
            };
            ctx?.signal('RsvpViewed', customDataForRsvpViewed, {
                actionSource: MAIL_READING_PANE_VIEW,
            });
        },
        [item, isLatestMeetingItemToShowRsvp]
    );

    const { selectedId } = getMailStore().mailModule;

    const removeCancelledEvent = useCallback(() => {
        deleteFromReadingPane(
            false /* shouldSupportUndoAction */,
            onReadingPaneClose,
            selectedId,
            false /* shouldIssueDeleteServiceCall */
        );
        return removeItemServiceProvider(item)();
    }, [item, selectedId, onReadingPaneClose]);

    return shouldMeetingItemDetailsShowRsvp(item) ? (
        <>
            <RsvpUnitWrapper
                meetingClientItem={item}
                onRsvpActionSuccess={deleteMeetingMessageCallback}
                shouldFetchFullEvent={shouldFetchFullEvent}
                isActionable={isLatestMeetingItemToShowRsvp}
                source={MAIL_READING_PANE_VIEW}
                stopTapPropagation
                onConversionToCalEvent={(calEvent: CalendarEvent) => {
                    const eventTime = getRsvpUnitTitle(calEvent);
                    const conflictInfo = getRsvpUnitSubtitle(calEvent, item);
                    const rsvpUnitAriaDetails = format(
                        strings.meetingInfo,
                        eventTime,
                        conflictInfo
                    );
                    if (!rsvpViewedYet?.current) {
                        logRsvpViewedDatapoint(calEvent);
                        rsvpViewedYet.current = true;
                    }
                    setRsvpUnitAriaDetailsForItem(item?.ItemId?.Id, rsvpUnitAriaDetails);
                }}
                removeCanceledEvent={removeCancelledEvent}
            />
        </>
    ) : null;
};

export default observer(ReadingPaneRsvpUnit);
